import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import styles from "./AddExerciseModal.module.scss";
import {
  useLazySearchExercizesQuery,
  useAddExercizeToTrainingMutation,
} from "@/store/trainingsApi";
import { ExercizeObj } from "@/store/trainingsApi";

type Approach = {
  approach_number: number;
  repetitions: number;
  repetitions_margin?: number;
  weight?: string;
};

type AddExerciseModalProps = {
  onClose: () => void;
  initData: string;
  trainingId: number;
  onSuccess: () => void;
  fromUser?: number;
};

export const AddExerciseModal: React.FC<AddExerciseModalProps> = ({
  onClose,
  initData,
  trainingId,
  onSuccess,
  fromUser,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ExercizeObj[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<ExercizeObj | null>(null);
  const [approaches, setApproaches] = useState<Approach[]>([
    {
      approach_number: 1,
      repetitions: 10,
      weight: "",
    },
  ]);

  const [searchExercizes] = useLazySearchExercizesQuery();
  const [addExercizeToTraining, { isLoading: isAdding }] = useAddExercizeToTrainingMutation();

  // Поиск упражнений с задержкой
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await searchExercizes({ q: searchQuery }).unwrap();
        setSearchResults(Array.isArray(result) ? result : []);
        setShowSearchResults(true);
      } catch (error) {
        console.error("Ошибка поиска упражнений:", error);
        setSearchResults([]);
        setShowSearchResults(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchExercizes]);

  // Закрываем результаты поиска при клике вне области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`.${styles.searchContainer}`)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchResults]);

  const handleSelectExercise = (exercise: ExercizeObj) => {
    setSelectedExercise(exercise);
    setSearchQuery(exercise.title);
    setShowSearchResults(false);
  };

  const handleAddApproach = () => {
    setApproaches([
      ...approaches,
      {
        approach_number: approaches.length + 1,
        repetitions: 10,
        weight: "",
      },
    ]);
  };

  const handleRemoveApproach = (index: number) => {
    const updated = approaches.filter((_, i) => i !== index);
    // Перенумеровываем подходы
    updated.forEach((approach, idx) => {
      approach.approach_number = idx + 1;
    });
    setApproaches(updated);
  };

  const handleUpdateApproach = (
    index: number,
    field: keyof Approach,
    value: string | number
  ) => {
    const updated = [...approaches];
    (updated[index][field] as any) = value;
    setApproaches(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedExercise) {
      toast.error("Выберите упражнение");
      return;
    }

    if (approaches.length === 0) {
      toast.error("Добавьте хотя бы один подход");
      return;
    }

    try {
      await addExercizeToTraining({
        training_id: trainingId,
        exercize_id: selectedExercise.id,
        body: {
          approaches: approaches.map((approach) => ({
            approach_number: approach.approach_number,
            repetitions: approach.repetitions,
            repetitions_margin: approach.repetitions_margin,
            weight: approach.weight || undefined,
          })),
          fromUser: fromUser,
        },
      }).unwrap();

      toast.success("Упражнение добавлено!");
      onClose();
      onSuccess();
    } catch (error: any) {
      console.error("Ошибка добавления упражнения:", error);
      toast.error(error?.data?.detail || "Ошибка при добавлении упражнения");
    }
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Добавить упражнение</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Поиск упражнений</label>
            <div className={styles.searchContainer}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Введите название упражнения"
                className={styles.input}
                onFocus={() => {
                  if (searchResults.length > 0) setShowSearchResults(true);
                }}
              />
              {isSearching && <div className={styles.loader}>Поиск...</div>}
              {showSearchResults && searchResults.length > 0 && (
                <div className={styles.searchResults}>
                  {searchResults.map((exercise) => (
                    <div
                      key={exercise.id}
                      className={styles.searchResultItem}
                      onClick={() => handleSelectExercise(exercise)}
                    >
                      <div className={styles.exerciseInfo}>
                        {exercise.title_photo && (
                          <img
                            src={exercise.title_photo}
                            alt={exercise.title}
                            className={styles.exerciseImage}
                          />
                        )}
                        <div>
                          <div className={styles.exerciseTitle}>{exercise.title}</div>
                          {exercise.muscle_group && (
                            <div className={styles.exerciseMuscleGroup}>{exercise.muscle_group}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedExercise && (
            <div className={styles.selectedExercise}>
              <div className={styles.exerciseCard}>
                {selectedExercise.title_photo && (
                  <img
                    src={selectedExercise.title_photo}
                    alt={selectedExercise.title}
                    className={styles.exerciseThumb}
                  />
                )}
                <div>
                  <div className={styles.exerciseName}>{selectedExercise.title}</div>
                  {selectedExercise.muscle_group && (
                    <div className={styles.exerciseMuscleGroup}>
                      {selectedExercise.muscle_group}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.approachesList}>
                <div className={styles.approachesHeader}>
                  <span>Подходы</span>
                  <button
                    type="button"
                    className={styles.addApproachBtn}
                    onClick={handleAddApproach}
                  >
                    + Добавить подход
                  </button>
                </div>
                {approaches.map((approach, index) => (
                  <div key={index} className={styles.approachRow}>
                    <span className={styles.approachNumber}>Подход {approach.approach_number}</span>
                    <input
                      type="number"
                      min="1"
                      value={approach.repetitions}
                      onChange={(e) =>
                        handleUpdateApproach(index, "repetitions", parseInt(e.target.value) || 0)
                      }
                      placeholder="Повторения"
                      className={styles.approachInput}
                    />
                    <input
                      type="text"
                      value={approach.weight || ""}
                      onChange={(e) => handleUpdateApproach(index, "weight", e.target.value)}
                      placeholder="Вес (кг)"
                      className={styles.approachInput}
                    />
                    {approaches.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeApproachBtn}
                        onClick={() => handleRemoveApproach(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isAdding || !selectedExercise}
            >
              {isAdding ? "Добавление..." : "Добавить упражнение"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

